import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Users, BarChart3, Eye, DollarSign, Heart, MessageCircle, Repeat2, Quote, Monitor, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from "@/contexts/AuthContext";

interface EngagementMetrics {
  retweets?: number;
  replies?: number;
  likes?: number;
  quotes?: number;
  impressions?: number;
  total?: number;
  last_updated?: string;
}

interface InternalKolTypeAnalyticsData {
  internal_kol_type: string;
  region: string;
  total_kols: number;
  total_posting_amount: number;
  total_post_views: number;
  total_expense: number;
  engagement_metrics: {
    total_retweets: number;
    total_replies: number;
    total_likes: number;
    total_quotes: number;
    total_impressions: number;
  };
}

interface RegionGroupData {
  region: string;
  types: InternalKolTypeAnalyticsData[];
  totalKols: number;
  totalPosts: number;
  totalViews: number;
  totalExpense: number;
  totalEngagement: {
    total_retweets: number;
    total_replies: number;
    total_likes: number;
    total_quotes: number;
    total_impressions: number;
  };
}

interface InternalKolTypeAnalyticsProps {
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

export const InternalKolTypeAnalytics: React.FC<InternalKolTypeAnalyticsProps> = ({ 
  dateFrom = null, 
  dateTo = null 
}) => {
  const [data, setData] = useState<RegionGroupData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const { hasFullAccess, hasBuAccess, userBu } = useAuth();

  const fetchInternalKolTypeAnalytics = async () => {
    try {
      setIsLoading(true);
      
      console.log('Fetching internal KOL type analytics...');
      
      // Build date filter conditions for kol_tracker query
      let dateFilter = '';
      const params: any = {};
      
      if (dateFrom) {
        dateFilter += ' AND kt.posting_date >= $dateFrom';
        params.dateFrom = dateFrom.toISOString().split('T')[0];
      }
      
      if (dateTo) {
        dateFilter += ' AND kt.posting_date <= $dateTo';
        params.dateTo = dateTo.toISOString().split('T')[0];
      }

      // Query to get analytics data grouped by internal_kol_type and region
      const { data: analyticsData, error } = await supabase
        .from('kol_db')
        .select(`
          internal_kol_type,
          region,
          kol_tracker!kol_tracker_kol_db_id_fkey (
            id,
            content_views,
            expense,
            engagement_metrics,
            posting_date
          )
        `)
        .not('internal_kol_type', 'is', null)
        .not('region', 'is', null);

      if (error) {
        console.error('Error fetching internal KOL type analytics:', error);
        throw error;
      }

      console.log('Raw analytics data:', analyticsData);

      // Process the data to group by region and then by internal_kol_type
      const processedData = new Map<string, Map<string, InternalKolTypeAnalyticsData>>();

      analyticsData?.forEach(kol => {
        const kolType = kol.internal_kol_type;
        const region = kol.region || 'Unknown Region';
        if (!kolType) return;

        if (!processedData.has(region)) {
          processedData.set(region, new Map());
        }

        const regionMap = processedData.get(region)!;
        const key = `${region}-${kolType}`;

        if (!regionMap.has(kolType)) {
          regionMap.set(kolType, {
            internal_kol_type: kolType,
            region: region,
            total_kols: 0,
            total_posting_amount: 0,
            total_post_views: 0,
            total_expense: 0,
            engagement_metrics: {
              total_retweets: 0,
              total_replies: 0,
              total_likes: 0,
              total_quotes: 0,
              total_impressions: 0,
            }
          });
        }

        const typeData = regionMap.get(kolType)!;
        typeData.total_kols += 1;

        // Process kol_tracker data with date filtering
        const trackerData = Array.isArray(kol.kol_tracker) ? kol.kol_tracker : [];
        
        trackerData.forEach(tracker => {
          // Apply date filtering
          if (dateFrom || dateTo) {
            const postingDate = tracker.posting_date ? new Date(tracker.posting_date) : null;
            if (dateFrom && postingDate && postingDate < dateFrom) return;
            if (dateTo && postingDate && postingDate > dateTo) return;
          }

          typeData.total_posting_amount += 1;
          typeData.total_post_views += tracker.content_views || 0;
          typeData.total_expense += tracker.expense || 0;

          // Parse engagement metrics
          if (tracker.engagement_metrics) {
            let metrics: EngagementMetrics = {};
            try {
              if (typeof tracker.engagement_metrics === 'string') {
                metrics = JSON.parse(tracker.engagement_metrics);
              } else {
                metrics = tracker.engagement_metrics as EngagementMetrics;
              }
              
              typeData.engagement_metrics.total_retweets += metrics.retweets || 0;
              typeData.engagement_metrics.total_replies += metrics.replies || 0;
              typeData.engagement_metrics.total_likes += metrics.likes || 0;
              typeData.engagement_metrics.total_quotes += metrics.quotes || 0;
              typeData.engagement_metrics.total_impressions += metrics.impressions || 0;
            } catch (e) {
              console.warn('Failed to parse engagement metrics:', tracker.engagement_metrics);
            }
          }
        });
      });

      // Convert to hierarchical structure
      const regionGroups: RegionGroupData[] = [];
      
      for (const [region, typesMap] of processedData.entries()) {
        const types = Array.from(typesMap.values())
          .filter(item => item.total_posting_amount > 0)
          .sort((a, b) => b.total_expense - a.total_expense);

        if (types.length > 0) {
          const regionGroup: RegionGroupData = {
            region,
            types,
            totalKols: types.reduce((sum, type) => sum + type.total_kols, 0),
            totalPosts: types.reduce((sum, type) => sum + type.total_posting_amount, 0),
            totalViews: types.reduce((sum, type) => sum + type.total_post_views, 0),
            totalExpense: types.reduce((sum, type) => sum + type.total_expense, 0),
            totalEngagement: {
              total_retweets: types.reduce((sum, type) => sum + type.engagement_metrics.total_retweets, 0),
              total_replies: types.reduce((sum, type) => sum + type.engagement_metrics.total_replies, 0),
              total_likes: types.reduce((sum, type) => sum + type.engagement_metrics.total_likes, 0),
              total_quotes: types.reduce((sum, type) => sum + type.engagement_metrics.total_quotes, 0),
              total_impressions: types.reduce((sum, type) => sum + type.engagement_metrics.total_impressions, 0),
            }
          };
          regionGroups.push(regionGroup);
        }
      }

      const finalData = regionGroups.sort((a, b) => b.totalExpense - a.totalExpense);

      console.log('Processed hierarchical internal KOL type data:', finalData);
      setData(finalData);
      
    } catch (error) {
      console.error('Error fetching internal KOL type analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInternalKolTypeAnalytics();
  }, [dateFrom, dateTo]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalKols = data.reduce((sum, region) => sum + region.totalKols, 0);
    const totalPosts = data.reduce((sum, region) => sum + region.totalPosts, 0);
    const totalViews = data.reduce((sum, region) => sum + region.totalViews, 0);
    const totalExpense = data.reduce((sum, region) => sum + region.totalExpense, 0);

    return {
      totalKols,
      totalPosts,
      totalViews,
      totalExpense
    };
  }, [data]);

  // Toggle region expansion
  const toggleRegion = (region: string) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };

  // Expand/collapse all regions
  const expandAll = () => {
    setExpandedRegions(new Set(data.map(region => region.region)));
  };

  const collapseAll = () => {
    setExpandedRegions(new Set());
  };

  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Internal KOL Type Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="relative min-h-[400px]">
          <TableLoadingOverlay isLoading={true} message="Loading internal KOL type analytics..." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-xl font-bold">
          INTERNAL KOL TYPE ANALYTICS
        </CardTitle>
        
        {/* Control Buttons */}
        <div className="flex gap-2 mb-4">
          <Button onClick={expandAll} variant="outline" size="sm">
            Expand All Regions
          </Button>
          <Button onClick={collapseAll} variant="outline" size="sm">
            Collapse All Regions
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="overflow-x-auto">
          {data.map((regionGroup) => (
            <div key={regionGroup.region} className="mb-6 border rounded-lg overflow-hidden">
              <Collapsible 
                open={expandedRegions.has(regionGroup.region)}
                onOpenChange={() => toggleRegion(regionGroup.region)}
              >
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-4 bg-muted hover:bg-muted/80 cursor-pointer transition-colors">
                    <div className="flex items-center gap-3">
                      {expandedRegions.has(regionGroup.region) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-semibold text-lg">{regionGroup.region}</span>
                      <Badge variant="secondary">
                        {regionGroup.types.length} type{regionGroup.types.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    
                    {/* Region Summary */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-blue-500" />
                        <span>{formatNumber(regionGroup.totalKols)} KOLs</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-3 w-3 text-green-500" />
                        <span>{formatNumber(regionGroup.totalPosts)} Posts</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3 text-purple-500" />
                        <span>{formatNumber(regionGroup.totalViews)} Views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3 text-orange-500" />
                        <span>{formatCurrency(regionGroup.totalExpense)}</span>
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Internal KOL Type</TableHead>
                          <TableHead className="text-center">Total KOLs</TableHead>
                          <TableHead className="text-center">Total Posts</TableHead>
                          <TableHead className="text-center">Total Views</TableHead>
                          <TableHead className="text-center">Total Engagement</TableHead>
                          <TableHead className="text-center">Total Expense</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {regionGroup.types.map((item) => (
                          <TableRow key={`${item.region}-${item.internal_kol_type}`}>
                            <TableCell className="font-medium">
                              <Badge variant="outline" className="text-sm">
                                {item.internal_kol_type}
                              </Badge>
                            </TableCell>
                            
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Users className="h-4 w-4 text-blue-500" />
                                <span className="font-medium">{formatNumber(item.total_kols)}</span>
                              </div>
                            </TableCell>
                            
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <BarChart3 className="h-4 w-4 text-green-500" />
                                <span className="font-medium">{formatNumber(item.total_posting_amount)}</span>
                              </div>
                            </TableCell>
                            
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Eye className="h-4 w-4 text-purple-500" />
                                <span className="font-medium">{formatNumber(item.total_post_views)}</span>
                              </div>
                            </TableCell>
                            
                            <TableCell className="text-center">
                              <div className="flex flex-wrap items-center justify-center gap-2">
                                {item.engagement_metrics.total_likes > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Heart className="h-3 w-3 text-red-500" />
                                    <span className="text-xs">{formatNumber(item.engagement_metrics.total_likes)}</span>
                                  </div>
                                )}
                                {item.engagement_metrics.total_retweets > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Repeat2 className="h-3 w-3 text-green-500" />
                                    <span className="text-xs">{formatNumber(item.engagement_metrics.total_retweets)}</span>
                                  </div>
                                )}
                                {item.engagement_metrics.total_replies > 0 && (
                                  <div className="flex items-center gap-1">
                                    <MessageCircle className="h-3 w-3 text-blue-500" />
                                    <span className="text-xs">{formatNumber(item.engagement_metrics.total_replies)}</span>
                                  </div>
                                )}
                                {item.engagement_metrics.total_quotes > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Quote className="h-3 w-3 text-yellow-500" />
                                    <span className="text-xs">{formatNumber(item.engagement_metrics.total_quotes)}</span>
                                  </div>
                                )}
                                {item.engagement_metrics.total_impressions > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Monitor className="h-3 w-3 text-gray-500" />
                                    <span className="text-xs">{formatNumber(item.engagement_metrics.total_impressions)}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <DollarSign className="h-4 w-4 text-orange-500" />
                                <span className="font-medium">{formatCurrency(item.total_expense)}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          ))}
          
          {data.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No internal KOL type data found for the selected filters.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};